import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    CircularProgress, 
    Alert,
    Snackbar,
    Collapse,
    Card,
    CardContent,
    Divider,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Autocomplete,
    Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
dayjs.extend(relativeTime);


const filterOptions = (options, state) => {
  const inputValue = state.inputValue.toLowerCase().trim();

  if (inputValue === "") {
    return []; 
  }

  return options.filter((option) =>
    option.company_name.toLowerCase().startsWith(inputValue)
  );
};

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();

  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    border: '1px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    boxShadow: 'none',
}));

const StyledTableRow = styled(TableRow)(({ theme, index }) => ({
    '& > td': {
        borderBottom: 'none',
    },
    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff', 
}));

const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
       <Typography
               variant="subtitle2"
               sx={{
                 fontWeight: "bold",
                 fontSize: "0.9rem",
                 color: "#11224d",
                 mb: 0.5,
                 borderBottom: '1px solid #ddd',
                 pb: 0.5,
               }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);


const HistoryRow = ({ truck, idx, onDelete, assignableParties = [], equipmentTypes, onOpenAssignDialog, selectionMode, isSelected, onSelect }) => {
    const [open, setOpen] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('MM/DD/YYYY');
    };

    const handleRowClick = (e) => {
        if (selectionMode) {
            onSelect(truck.id);
        } else {
            setOpen(!open);
        }
    };
    
    const truckDetails = truck.details || {};

    const assignedParty = truck.shipper_id ?
        assignableParties.find(p => p.id === truck.shipper_id && p.type === 'shipper') :
        truck.broker_id ?
        assignableParties.find(p => p.id === truck.broker_id && p.type === 'broker') : null;


    return (
        <>
            <StyledTableRow 
                index={idx} 
                hover 
                onClick={handleRowClick} 
                sx={{ cursor: 'pointer' }}
            >
                <TableCell padding="checkbox" sx={{ borderBottom: open ? 'none' : null, py: 0.5 }}>
                    {selectionMode ? (
                        <Checkbox checked={isSelected} onClick={e => e.stopPropagation()} onChange={() => onSelect(truck.id)} />
                    ) : (
                        <IconButton size="small" sx={{ p: 0, color: 'primary.main' }} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem',py: 0.7}}>
                    {formatDate(truck.availableDate)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.7}}>{truck.origin}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.7}}>{truck.destination}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 0.7 }}>
                    {(truck.equipment || truck.truckType || '').split(',').map(e => {
                        const eq = equipmentTypes.find(et => et.label === e.trim());
                        return eq ? eq.short : e;
                    }).join(', ')}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', py: 0.7}}>
                    {truck.companyState}
                </TableCell>
               

<TableCell sx={{ py: 0.7, textAlign: 'center' }}>
    {assignedParty ? (
        <Typography
            variant="body2"
            sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#2e7d32',
                textAlign: 'center',
                display: 'inline-block'
            }}
        >
            {assignedParty.company_name}
            {assignedParty.type === 'broker' ? (
                <>
                    {assignedParty.mc_number ? (
                        <> (MC: {assignedParty.mc_number})</>
                    ) : assignedParty.dot_number ? (
                        <> (DOT: {assignedParty.dot_number})</>
                    ) : assignedParty.registration_number ? (
                        <> ({getRegistrationType(assignedParty.registration_number)}: {assignedParty.registration_number})</>
                    ) : null}
                </>
            ) : (
                <>
                    {assignedParty.registration_number && (
                        <> ({getRegistrationType(assignedParty.registration_number)}: {assignedParty.registration_number})</>
                    )}
                    {!assignedParty.registration_number && assignedParty.type === 'shipper' && (
                        <> (Shipper)</>
                    )}
                </>
            )}
        </Typography>
    ) : (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '200px' }}>
            <Autocomplete
                options={assignableParties}
                getOptionLabel={(option) => {
                    if (option.type === 'broker') {
                        let label = option.company_name;
                        if (option.mc_number) label += ` (MC: ${option.mc_number})`;
                        if (option.dot_number) label += ` (DOT: ${option.dot_number})`;
                        if (!option.mc_number && !option.dot_number && option.registration_number) {
                             label += ` (${getRegistrationType(option.registration_number)}: ${option.registration_number})`;
                        }
                        return label;
                    }
                    return `${option.company_name} (${option.type === 'shipper' ? 'Shipper' : 'Broker'})`;
                }}
                filterOptions={filterOptions}
                size="small"
                sx={{ minWidth: 200, maxWidth: 250 }}
                onClick={(e) => e.stopPropagation()}
                onChange={async (event, newValue) => {
                    if (!newValue) return;
                    onOpenAssignDialog(truck.id, newValue, newValue.type);
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Search Shipper/Broker" 
                        variant="outlined" 
                        sx={{ 
                            fontSize: '0.75rem', 
                            '.MuiInputBase-root': { fontSize: '0.75rem', p: 1 }, 
                            '.MuiInputLabel-root': { fontSize: '0.75rem' } 
                        }} 
                    />
                )}
            />
        </Box>
    )}
</TableCell>
                
                <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                    {!selectionMode && (
                        <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(truck.id); }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </TableCell>
            </StyledTableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
    <Card
        sx={{
            border: "1px solid #f0f0f0", 
            borderRadius: 1, 
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            backgroundColor: "#ffffff",
        }}
    >
        <CardContent sx={{ p: 1.5 }}>
            
            <Typography
                variant="body2"
                sx={{
                    fontWeight: "bold",
                    mb: 1,
                    fontSize: "1rem", 
                    color: "#11224d", 
                }}
            >
                {truck.origin} → {truck.destination}
            </Typography>
            <Divider sx={{ my: 1 }} /> 

            <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                
                <Box sx={{ flex: '3 1 0%', pr: 3 }}>
                    <DetailSection title="Truck Details">
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                               
                    <Box sx={{ mr: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                       
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                border: '2px solid #1976d2',
                                backgroundColor: 'white',
                            }}
                        />
                       
                        <Box
                            sx={{
                                height: 49,
                                borderLeft: '1px dashed #bdbdbd',
                            }}
                        />
                        
                        <Box
                            sx={{
                                width: 9,
                                height: 9,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                            }}
                        />
                    </Box>
                          
                            <Box>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: "bold" }}>{truck.origin}</span>
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                                    {formatDate(truck.availableDate)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mt: 2 }}>
                                    {truck.destination}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Equipment: </span>{truck.equipment || truck.truckType || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Load: </span>{truck.fullOrPartial || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Feet: </span>{truck.length ? `${truck.length} ft` : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Weight: </span>{truck.weight ? `${truck.weight} lbs` : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Reference ID: </span>{truck.referenceId || 'N/A'}
                            </Typography>
                        </Box>
                    </DetailSection>
                </Box>

                <Box sx={{ flex: '5 1 0%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        
                        <Box sx={{ flex: '1 1 50%' }}>
                            <DetailSection title="Company">
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Company Name: </span>{truckDetails.companyName || 'N/A'}
                                </Typography>
                                
                               <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: 'bold' }}>
        {getRegistrationType(truck.registration_number)} :
    </span>{" "}
    {truck.registration_number || 'N/A'}
</Typography>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Contact: </span>{truckDetails.contactInfo || 'N/A'}
                                </Typography>
                            </DetailSection>
                        </Box>
                       {assignedParty && (
    <Box sx={{ mt: 2 }}>
       <DetailSection title={`Assigned ${assignedParty.type.charAt(0).toUpperCase() + assignedParty.type.slice(1)}`}>
            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: 'bold' }}>Company Name: </span>
                {assignedParty.company_name || 'N/A'}
            </Typography>
           {assignedParty.type === 'broker' ? (
                <>
                    {assignedParty.mc_number ? (
                        <> (MC: {assignedParty.mc_number})</>
                    ) : assignedParty.dot_number ? (
                        <> (DOT: {assignedParty.dot_number})</>
                    ) : assignedParty.registration_number ? (
                        <> ({getRegistrationType(assignedParty.registration_number)}: {assignedParty.registration_number})</>
                    ) : null}
                </>
            ) : (
            
                /* Show registration number for shippers/others if available */
                assignedParty.registration_number && (
                    <Typography variant="caption" sx={{ display: "block" }}>
                        <span style={{ fontWeight: 'bold' }}>
                            {getRegistrationType(assignedParty.registration_number)} Number: 
                        </span>
                        {assignedParty.registration_number}
                    </Typography>
                )
            )}
            
            {assignedParty.type === 'shipper' && assignedParty.dot_number && !assignedParty.registration_number && (
                <Typography variant="caption" sx={{ display: "block" }}>
                    <span style={{ fontWeight: 'bold' }}>
                        {getRegistrationType(assignedParty.dot_number)} Number: 
                    </span>
                    {assignedParty.dot_number}
                </Typography>
            )}
            
            
        </DetailSection>
    </Box>
)}
                    </Box>  
                    
                    <DetailSection title="Notes">
                        <Typography variant="caption">
                            {truck.comments || "No additional notes provided."}
                        </Typography>
                    </DetailSection>
                </Box>
            </Box>
        </CardContent>
    </Card>
</Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const TruckHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [equipmentTypes, setEquipmentTypes] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const limit = 50;
    const [selectionMode, setSelectionMode] = useState(false);
    const [selected, setSelected] = useState([]);

    const [assignableParties, setAssignableParties] = useState([]);

    useEffect(() => {
        const fetchAssignableParties = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || '';
                const shippersPromise = axios.get(`${API_BASE}/api/trucks/shippers`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const brokersPromise = axios.get(`${API_BASE}/api/trucks/brokers`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const [shippersRes, brokersRes] = await Promise.all([shippersPromise, brokersPromise]);

                const shippers = shippersRes.data.map(s => ({ ...s, type: 'shipper' }));
                const brokers = brokersRes.data.map(b => ({ ...b, type: 'broker' }));

                setAssignableParties([...shippers, ...brokers]);
            } catch (err) {
                console.error('Failed to load shippers or brokers', err);
                setError('Failed to load assignable parties.');
            }
        };
        fetchAssignableParties();
    }, []);

    const handleAssignShipper = (truckId, shipperId) => {
        setHistory(prevHistory => prevHistory.map(truck => truck.id === truckId ? { ...truck, shipper_id: shipperId } : truck));
    };

    const handleAssignBroker = (truckId, brokerId) => {
        setHistory(prevHistory => prevHistory.map(truck => truck.id === truckId ? { ...truck, broker_id: brokerId } : truck));
    };

  
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleAssignAction = async (truckId, party, type) => {
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE = process.env.REACT_APP_API_BASE || '';
            if (type === 'shipper') {
                await axios.put(
                    `${API_BASE}/api/trucks/${truckId}/assignShipper`,
                    { shipperId: party.id },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                setSnackbar({ open: true, message: '✅ Shipper assigned successfully!', severity: 'success' });
                handleAssignShipper(truckId, parseInt(party.id));
            } else if (type === 'broker') {
                await axios.put(
                    `${API_BASE}/api/trucks/${truckId}/assignBroker`,
                    { brokerId: party.id },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                setSnackbar({ open: true, message: '✅ Broker assigned successfully!', severity: 'success' });
                handleAssignBroker(truckId, parseInt(party.id));
            }
            handleCloseDialog();
        } catch (err) {
            console.error(`Failed to assign ${type}:`, err);
            setSnackbar({ open: true, message: `Error assigning ${type}. Please try again.`, severity: 'error' });
            handleCloseDialog();
        }
    };

    const openAssignDialog = (truckId, party, type) => {
        const partyType = type.charAt(0).toUpperCase() + type.slice(1);
        setConfirmDialog({
            open: true,
            title: `Confirm ${partyType} Assignment`,
            message: `Are you sure you want to assign this truck to "${party.company_name}"? You won't be able to change it later.`,
            onConfirm: () => handleAssignAction(truckId, party, type),
            confirmText: `Assign ${partyType}`
        });
    };

    useEffect(() => {
        const getEquipment = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const { data } = await axios.get(`${API_BASE}/api/public/equipment`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                setEquipmentTypes(data);
            } catch (error) {
                console.error("Could not load equipment types for dropdown.", error);
            }
        };
        getEquipment();
    }, []);

    const handleCloseDialog = () => {
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    const fetchHistory = async (pageNum) => {
        if (pageNum === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            const API_BASE = process.env.REACT_APP_API_BASE || '';
            const endpoint = `${API_BASE}/api/trucks/history?page=${pageNum}&limit=${limit}`;

            const response = await axios.get(endpoint, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const newResults = response.data.results || [];

            setHistory(prev => pageNum === 1 ? newResults : [...prev, ...newResults]);
            setHasMore(newResults.length === limit);
            setPage(pageNum);

        } catch (err) {
            console.error("Failed to fetch history:", err);
            const message = err.response?.data?.message || err.message || 'Failed to fetch truck history. Check server connection.';
            setError(message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchHistory(1);
    }, []);

    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            fetchHistory(page + 1);
        }
    };

    const observer = useRef();
    const lastTruckElementRef = useCallback(node => {
        if (loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                handleLoadMore();
            }
        });
        if (node) observer.current.observe(node);
    }, [loadingMore, hasMore, handleLoadMore]);

     const handleEnterSelectionMode = (id) => {
        setSelectionMode(true);
        setSelected([id]);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelected([]);
    };

    const handleSelect = (truckId) => {
        const selectedIndex = selected.indexOf(truckId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, truckId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = history.map((t) => t.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleDeleteRequest = (truckIds) => {
        const message = truckIds.length > 1
            ? `Are you sure you want to permanently delete these ${truckIds.length} truck records? This action cannot be undone.`
            : 'Are you sure you want to permanently delete this truck record? This action cannot be undone.';

        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: message,
            onConfirm: () => handleConfirmDelete(truckIds),
            confirmText: 'Confirm Delete'
        });
    };

    const handleConfirmDelete = async (idsToDelete) => {
        if (idsToDelete.length === 0) return;
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE = process.env.REACT_APP_API_BASE || '';
            
            const deletePromises = idsToDelete.map(id => 
                axios.delete(`${API_BASE}/api/trucks/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            );
            await Promise.all(deletePromises);

            setHistory(h => h.filter(t => !idsToDelete.includes(t.id)));
            setSnackbar({ open: true, message: '✅ Records deleted successfully!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Error deleting records. Please try again.', severity: 'error' });
            console.error('Bulk delete error:', err);
        } finally {
            handleCloseDialog();
            handleExitSelectionMode();
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#11224d' }}>
                    Total Past Trucks: {history.length}
                </Typography>
                {selectionMode ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteRequest(selected)}
                            disabled={selected.length === 0}
                        >
                            Delete ({selected.length})
                        </Button>
                        <Button size="small" onClick={handleExitSelectionMode}>Cancel</Button>
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectionMode(true)}
                        disabled={history.length === 0}
                    >
                        Select
                    </Button>
                )}
            </Box>

            <TableContainer component={StyledPaper} elevation={0} sx={{ maxHeight: 'calc(100vh - 130px)', overflow: 'auto' }}>
                <Table sx={{ minWidth: 850 }} aria-label="truck history table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d' }}>
                                {selectionMode && <Checkbox sx={{color: 'white'}} indeterminate={selected.length > 0 && selected.length < history.length} checked={history.length > 0 && selected.length === history.length} onChange={handleSelectAll} />}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Available Date</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Origin</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Destination</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Equipment</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Base</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Covered With</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '0.7rem', py: 0.5, backgroundColor: '#11224d' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                   <TableBody>
                        {history.length > 0 ? (
                            history.map((truck, idx) => (
                                <React.Fragment key={truck.id}>
                                    <HistoryRow 
                                        truck={truck} 
                                        idx={idx} 
                                        assignableParties={assignableParties}
                                        onAssignShipper={handleAssignShipper}
                                        onOpenAssignDialog={openAssignDialog}
                                        equipmentTypes={equipmentTypes}
                                        onDelete={handleEnterSelectionMode}
                                        selectionMode={selectionMode}
                                        isSelected={selected.indexOf(truck.id) !== -1}
                                        onSelect={handleSelect}
                                    />
                                    {idx === history.length - 1 && (
                                        <TableRow ref={lastTruckElementRef}>
                                            <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                                                {loadingMore && <CircularProgress size={24} />}
                                                {!hasMore && (
                                                    <Typography variant="caption" color="text.secondary">End of history</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        ) : !loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        You have no completed or past trucks in your history.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
                maxWidth="xs" 
                fullWidth 
            >
                <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 700 }}>
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
                        {confirmDialog.confirmText || 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TruckHistory;